import React, { useState, useRef, useEffect } from 'react';
import { Search, Car, Sparkles, Clock, ArrowRight, MessageCircle, ExternalLink, X, RotateCcw, CheckCircle2 } from 'lucide-react';
import { CATEGORIES, ATTRACTIONS } from '../data/attractions';
import { Attraction, Category } from '../types';
import { EASY_WAY_CONTACT } from '../data/contactInfo';
import { useLanguage } from '../context/LanguageContext';
import { getLocalizedAttraction } from '../utils/localization';

interface HeroProps {
  searchQuery: string;
  onSearchChange: (q: string) => void;
  selectedCategory: Category;
  onCategorySelect: (cat: Category) => void;
  onOpenCalculator: () => void;
  onAttractionClick?: (attr: Attraction) => void;
}

const CAROUSEL_IMAGES = [
  '/media/dominios/cancunmexico.travel/carrusel/portada-tulum.webp',
  '/media/dominios/cancunmexico.travel/carrusel/portada-arrecife.webp',
  '/media/dominios/cancunmexico.travel/carrusel/portada-arrecife.webp'
];

export const Hero: React.FC<HeroProps> = ({
  searchQuery,
  onSearchChange,
  selectedCategory,
  onCategorySelect,
  onOpenCalculator,
  onAttractionClick
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isInteracted, setIsInteracted] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const { lang, easyWayCancunAirportUrl, easyWayWhatsAppUrl, t } = useLanguage();

  // Activate carousel on first user interaction
  useEffect(() => {
    const handleInteraction = () => {
      setIsInteracted(true);
    };
    const events: (keyof WindowEventMap)[] = ['pointerdown', 'touchstart', 'keydown', 'wheel', 'scroll'];
    events.forEach(ev => window.addEventListener(ev, handleInteraction, { once: true, passive: true }));
    return () => {
      events.forEach(ev => window.removeEventListener(ev, handleInteraction));
    };
  }, []);

  // Carousel timer - ONLY active after user interaction
  useEffect(() => {
    if (!isInteracted) return;
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % CAROUSEL_IMAGES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [isInteracted]);

  // Close search dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Compute category counts
  const categoryCounts = React.useMemo(() => {
    const counts: Record<string, number> = { todos: ATTRACTIONS.length };
    for (const cat of CATEGORIES) {
      if (cat.id !== 'todos') {
        counts[cat.id] = ATTRACTIONS.filter(a => a.category === cat.id).length;
      }
    }
    return counts;
  }, []);

  // Compute live search matches for instant dropdown
  const trimmedSearch = searchQuery.trim().toLowerCase();
  const searchMatches = React.useMemo(() => {
    if (!trimmedSearch) return [];
    return ATTRACTIONS.map(a => getLocalizedAttraction(a, lang)).filter((item) => {
      return (
        item.title.toLowerCase().includes(trimmedSearch) ||
        item.shortDescription.toLowerCase().includes(trimmedSearch) ||
        item.tag.toLowerCase().includes(trimmedSearch) ||
        item.category.toLowerCase().includes(trimmedSearch) ||
        item.whyByCar.toLowerCase().includes(trimmedSearch)
      );
    });
  }, [trimmedSearch, lang]);

  const scrollToDestinos = () => {
    setIsDropdownOpen(false);
    setTimeout(() => {
      const el = document.getElementById('destinos');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 50);
  };

  const handleCategoryClick = (catId: Category) => {
    onCategorySelect(catId);
    setIsDropdownOpen(false);
    scrollToDestinos();
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    scrollToDestinos();
  };

  return (
    <section className="relative overflow-hidden bg-[#073B4C] text-white min-h-[560px] flex items-center">
      {/* Background image overlay - LCP slide 1 eager initially, extra slides load ONLY upon user interaction */}
      {isInteracted ? (
        CAROUSEL_IMAGES.map((img, idx) => (
          <img 
            key={`carousel-bg-${idx}`}
            src={img}
            alt="Cancún México Paisaje"
            width={1920}
            height={1080}
            loading={idx === 0 ? 'eager' : 'lazy'}
            fetchPriority={idx === 0 ? 'high' : 'low'}
            decoding="async"
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 pointer-events-none ${
              idx === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ))
      ) : (
        <img 
          src={CAROUSEL_IMAGES[0]}
          alt="Cancún México Paisaje"
          width={1920}
          height={1080}
          loading="eager"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        />
      )}

      {/* Modern gradient scrims to ensure high legibility and deep contrast */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#073B4C]/95 via-[#073B4C]/60 to-transparent pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#073B4C]/90 via-transparent to-transparent pointer-events-none" />

      {/* Atmospheric colored highlights */}
      <div className="absolute -top-24 left-1/4 w-[500px] h-[500px] bg-[#0F766E]/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 right-1/4 w-[500px] h-[500px] bg-[#D6A84B]/15 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 w-full">
        <div className="flex flex-col items-center text-center max-w-4xl mx-auto space-y-8">
          
          {/* Top Badges */}
          <div className="flex flex-wrap items-center justify-center gap-3">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold bg-[#0F766E]/40 text-white border border-[#0F766E]/60 backdrop-blur-md">
              <Sparkles className="w-3.5 h-3.5 text-[#D6A84B]" />
              <span>{t.hero.tagline}</span>
            </div>
            <a
              href={easyWayCancunAirportUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#D6A84B]/20 text-[#D6A84B] border border-[#D6A84B]/40 hover:bg-[#D6A84B]/30 transition-colors backdrop-blur-md"
            >
              <Car className="w-3.5 h-3.5 text-[#D6A84B]" />
              <span>{t.hero.recommendationTag}</span>
              <ExternalLink className="w-3 h-3 text-[#D6A84B]" />
            </a>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight font-['Outfit',sans-serif] leading-[1.15]">
            {t.hero.headlinePrefix}
            <br className="hidden sm:block" />
            <span className="text-[#D6A84B]">
              {t.hero.headlineHighlight}
            </span>
          </h1>

          {/* Subheading / Description */}
          <p className="text-sm sm:text-base md:text-lg text-[#F7F3EA]/90 font-normal leading-relaxed max-w-2xl mx-auto">
            {t.hero.subheading}
          </p>

            {/* Search Box with Interactive Form & Live Results Dropdown */}
            <div ref={searchContainerRef} className="relative max-w-2xl">
              <form 
                onSubmit={handleSearchSubmit}
                className="bg-[#052a37]/90 backdrop-blur-md p-2 rounded-2xl border border-white/20 shadow-2xl flex flex-col sm:flex-row gap-2"
              >
                <div className="relative flex-1 flex items-center">
                  <Search className="w-5 h-5 text-[#F7F3EA]/50 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    type="text"
                    value={searchQuery}
                    onFocus={() => {
                      if (trimmedSearch) setIsDropdownOpen(true);
                    }}
                    onChange={(e) => {
                      onSearchChange(e.target.value);
                      setIsDropdownOpen(true);
                    }}
                    placeholder={t.hero.searchPlaceholder}
                    className="w-full pl-11 pr-10 py-3 bg-[#073B4C] text-white placeholder-[#F7F3EA]/70 rounded-xl text-sm font-medium border border-white/20 focus:outline-none focus:border-[#0F766E] focus:ring-1 focus:ring-[#0F766E] transition-all"
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => {
                        onSearchChange('');
                        setIsDropdownOpen(false);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/60 hover:text-white p-1 rounded-md transition-colors cursor-pointer"
                      title={t.hero.clearSearch}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-xs sm:text-sm font-bold text-white bg-[#0F766E] hover:bg-[#0d695d] active:scale-95 transition-all rounded-xl shadow-md cursor-pointer whitespace-nowrap"
                >
                  <Search className="w-4 h-4" />
                  <span>{lang === 'en' ? 'Search' : lang === 'pt' ? 'Buscar Destinos' : 'Buscar Destinos'}</span>
                </button>
              </form>

              {/* Live Instant Search Dropdown Preview */}
              {isDropdownOpen && trimmedSearch.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-2 bg-white text-[#172A32] rounded-2xl border border-[#ede6d8] shadow-2xl z-30 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="px-4 py-2.5 bg-[#F7F3EA] border-b border-[#ede6d8] flex items-center justify-between text-xs">
                    <span className="font-bold text-[#073B4C]">
                      {searchMatches.length > 0
                        ? `${searchMatches.length} ${searchMatches.length === 1 ? 'resultado encontrado' : 'resultados encontrados'}:`
                        : 'Sin coincidencias directas'}
                    </span>
                    <button
                      type="button"
                      onClick={() => setIsDropdownOpen(false)}
                      className="text-[#4A5A5F] hover:text-[#172A32] font-semibold text-[11px] cursor-pointer"
                    >
                      Cerrar
                    </button>
                  </div>

                  {searchMatches.length > 0 ? (
                    <div className="divide-y divide-[#ede6d8] max-h-72 overflow-y-auto">
                      {searchMatches.slice(0, 5).map((item) => (
                        <div
                          key={item.id}
                          onClick={() => {
                            setIsDropdownOpen(false);
                            if (onAttractionClick) {
                              onAttractionClick(item);
                            } else {
                              scrollToDestinos();
                            }
                          }}
                          className="p-3.5 hover:bg-[#F7F3EA] transition-colors cursor-pointer flex items-center gap-3 group"
                        >
                          <img
                            src={item.image}
                            alt={item.title}
                            width={48}
                            height={48}
                            loading="lazy"
                            decoding="async"
                            className="w-12 h-12 rounded-lg object-cover shrink-0 border border-[#ede6d8]"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-xs sm:text-sm font-bold text-[#172A32] group-hover:text-[#0F766E] truncate transition-colors">
                                {item.title}
                              </p>
                              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-[#044e45]/10 text-[#044e45] shrink-0">
                                {item.tag}
                              </span>
                            </div>
                            <p className="text-[11px] text-[#4A5A5F] truncate mt-0.5">
                              {item.distanceFromCancun} • {item.priceRange} • ⭐ {item.rating}
                            </p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-[#0F766E] shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-xs text-[#4A5A5F]">
                      <p className="font-semibold text-sm text-[#172A32]">No encontramos destinos para "{searchQuery}"</p>
                      <p className="mt-1">Prueba con palabras como "Playa", "Cenote", "Tulum", "Cobá" o "Shopping".</p>
                      <button
                        type="button"
                        onClick={() => {
                          onSearchChange('');
                          onCategorySelect('todos');
                          scrollToDestinos();
                        }}
                        className="mt-3 px-4 py-1.5 text-xs font-bold text-white bg-[#0F766E] hover:bg-[#0d695d] rounded-lg cursor-pointer"
                      >
                        Ver todos los lugares
                      </button>
                    </div>
                  )}

                  {searchMatches.length > 0 && (
                    <div className="p-2.5 bg-[#F7F3EA] border-t border-[#ede6d8] text-center">
                      <button
                        type="button"
                        onClick={scrollToDestinos}
                        className="text-xs font-bold text-[#0F766E] hover:text-[#0d695d] inline-flex items-center gap-1 cursor-pointer transition-colors"
                      >
                        <span>Ver todos los {searchMatches.length} lugares en el catálogo inferior</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Sub-action: Savings simulator link */}
              <div className="mt-2.5 flex items-center justify-between text-xs text-[#F7F3EA]/80 px-1">
                <span>{lang === 'en' ? 'Want to estimate your transport and fuel budget?' : lang === 'pt' ? 'Quer cotizar seu traslado e combustível?' : '¿Quieres cotizar tu traslado y combustible?'}</span>
                <button
                  type="button"
                  onClick={onOpenCalculator}
                  className="font-bold text-[#D6A84B] hover:text-white underline inline-flex items-center gap-1 cursor-pointer"
                >
                  <span>{lang === 'en' ? 'Savings Calculator: Car vs Taxi' : lang === 'pt' ? 'Calculadora de Economia Carro vs Táxi' : 'Calculadora de Ahorro Auto vs Taxi'}</span>
                  <ArrowRight className="w-3 h-3" />
                </button>
              </div>
            </div>

            {/* Category Quick Filter Chips with item counts and smooth scroll */}
            <div className="pt-2">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-bold text-[#F7F3EA]/90 uppercase tracking-wider">
                  {lang === 'en' ? 'Filter by category:' : lang === 'pt' ? 'Filtrar por categoria de destino:' : 'Filtrar por categoría de destino:'}
                </p>
                {selectedCategory !== 'todos' && (
                  <button
                    type="button"
                    onClick={() => handleCategoryClick('todos')}
                    className="text-[11px] font-bold text-[#D6A84B] hover:underline flex items-center gap-1 cursor-pointer"
                  >
                    <RotateCcw className="w-3 h-3" />
                    <span>{t.categories.todos}</span>
                  </button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {CATEGORIES.map((cat) => {
                  const isActive = selectedCategory === cat.id;
                  const count = categoryCounts[cat.id] || 0;
                  const translatedLabel = t.categories[cat.id as keyof typeof t.categories] || cat.label;
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => handleCategoryClick(cat.id as Category)}
                      className={`px-3.5 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer flex items-center gap-1.5 shadow-xs ${
                        isActive
                          ? 'bg-[#0F766E] text-white font-bold shadow-md ring-2 ring-[#D6A84B] scale-102'
                          : 'bg-white/10 text-[#F7F3EA] hover:bg-white/20 border border-white/15'
                      }`}
                    >
                      <span>{translatedLabel}</span>
                      <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-extrabold ${
                        isActive ? 'bg-[#073B4C] text-[#D6A84B]' : 'bg-white/20 text-white'
                      }`}>
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
        </div>
      </div>
    </section>
  );
};

